import { useEffect } from "react";
import { useLocation, useSearch } from "wouter";
import { Loader2 } from "lucide-react";
import { processReferralConversion } from "@/hooks/useReferralCapture";
import { useAuth } from "@/hooks/useAuth";

export default function AuthCallback() {
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const queryString = searchString || window.location.search;
  const params = new URLSearchParams(queryString);
  const { refreshUser } = useAuth();
  
  const token = params.get("token");
  const provider = params.get("provider");
  const queryReturnUrl = params.get("returnUrl");
  const storedReturnUrl = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('auth_return_url') : null;
  const returnUrl = queryReturnUrl || storedReturnUrl || "/";
  const authError = params.get("auth_error") || params.get("error");

  useEffect(() => {
    const handleAuth = async () => {
      console.log("[AuthCallback] Initializing...", { token: token ? "present" : "missing", provider });

      if (authError) {
        console.error("[AuthCallback] OAuth error:", authError);
        localStorage.removeItem("eatzy_auth_token");
        setLocation(`/login?error=${encodeURIComponent(authError)}`);
        return;
      }

      if (!token) {
        console.error("[AuthCallback] No token in URL");
        setLocation("/login?error=authentication_failed");
        return;
      }

      console.log(`[AuthCallback] Verifying token from ${provider} OAuth...`);

      // Call /api/auth/verify directly (instead of authService.verifyToken) so we can
      // read the full response body, including eatzyAccessToken if the auth service provides it.
      // This allows social login users to store their Eatzy token the same way email/password users do.
      const attemptVerify = async (tokenToTry: string): Promise<{ verifiedUser: any; data: any } | null> => {
        try {
          const resp = await fetch('/api/auth/verify', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: tokenToTry }),
          });
          if (!resp.ok) return null;
          const data = await resp.json();
          if (data.valid && data.user) return { verifiedUser: data.user, data };
          return null;
        } catch (e) {
          console.error("[AuthCallback] Verify fetch error:", e);
          return null;
        }
      };

      let workingToken = token;
      let result: { verifiedUser: any; data: any } | null = null;

      let tokenToVerify = token;
      if (token.includes('%')) {
        try {
          tokenToVerify = decodeURIComponent(token);
        } catch (e) {
          console.warn("[AuthCallback] Failed to decode token:", e);
        }
      }

      result = await attemptVerify(tokenToVerify);
      if (result) {
        workingToken = tokenToVerify;
      } else if (tokenToVerify !== token) {
        console.log("[AuthCallback] Retrying verification with original token...");
        result = await attemptVerify(token);
        if (result) workingToken = token;
      }

      if (result) {
        const { verifiedUser, data } = result;
        console.log("[AuthCallback] Token verified successfully for user:", verifiedUser.email);

        sessionStorage.removeItem('login_error');
        sessionStorage.removeItem('auth_return_url');

        localStorage.setItem("auth_token", workingToken);
        localStorage.setItem("auth_user", JSON.stringify(verifiedUser));
        localStorage.setItem("eatzy_auth_token", workingToken);
        localStorage.setItem("eatzy_auth_provider", provider || "unknown");

        // Store Eatzy access token if auth service returned one (enables Eatzy API calls for social users)
        if (data.eatzyAccessToken) {
          console.log("[AuthCallback] Storing eatzyAccessToken for social login user");
          localStorage.setItem('eatzyUserToken', data.eatzyAccessToken);
          sessionStorage.setItem('adminEatzyToken', data.eatzyAccessToken);
          sessionStorage.setItem('partnerEatzyToken', data.eatzyAccessToken);
          if (data.eatzyRefreshToken) localStorage.setItem('eatzyRefreshToken', data.eatzyRefreshToken);
          if (data.eatzyTokenExpires) localStorage.setItem('eatzyTokenExpires', String(data.eatzyTokenExpires));
        }

        await refreshUser();
        await processReferralConversion();
        setLocation(returnUrl);
      } else {
        console.error("[AuthCallback] Token verification returned null");
        setLocation("/login?error=verification_failed");
      }
    };
    
    handleAuth();
  }, [token, provider, returnUrl, authError, setLocation]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-4">
      <Loader2 className="h-8 w-8 animate-spin text-primary" data-testid="loader-auth" />
      <p className="text-muted-foreground" data-testid="text-auth-processing">
        {authError ? "Authentication failed..." : "Completing sign in..."}
      </p>
    </div>
  );
}
